import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Crown, Loader2, CheckCircle2, XCircle } from "lucide-react";

export default function ClubSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { user } = useAuth();
  const [status, setStatus] = useState("polling");
  const [attempts, setAttempts] = useState(0);
  const [membership, setMembership] = useState(null);

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    if (!user) { setStatus("login_required"); return; }

    let cancelled = false;
    const poll = async (n) => {
      if (cancelled || n > 10) {
        if (!cancelled) setStatus("timeout");
        return;
      }
      setAttempts(n);
      try {
        const r = await api.get(`/club/subscribe/status/${sessionId}`);
        if (r.data.payment_status === "paid" || r.data.membership_active) {
          setMembership(r.data.membership);
          setStatus("success");
          return;
        }
        if (r.data.status === "expired") { setStatus("expired"); return; }
        setTimeout(() => poll(n + 1), 2000);
      } catch (e) {
        setTimeout(() => poll(n + 1), 2000);
      }
    };
    poll(1);
    return () => { cancelled = true; };
  }, [sessionId, user]);

  return (
    <div data-testid="club-success-page" className="bg-[#0a0a0a] text-white min-h-[85vh] flex items-center">
      <div className="container-xl max-w-xl text-center">
        {status === "polling" && (
          <div>
            <Loader2 className="h-12 w-12 mx-auto mb-8 animate-spin text-[#C9A961]" />
            <h1 className="editorial-heading text-4xl font-light mb-4">Stiamo confermando la tua iscrizione…</h1>
            <p className="text-white/60">Verifica in corso del pagamento Stripe (tentativo {attempts}/10)</p>
          </div>
        )}
        {status === "success" && (
          <div data-testid="club-success-ok">
            <Crown className="h-16 w-16 mx-auto mb-8 text-[#C9A961]" strokeWidth={1} />
            <div className="text-xs uppercase tracking-[0.4em] text-[#C9A961] mb-4">Benvenuto nel Club</div>
            <h1 className="editorial-heading text-5xl font-light mb-6">
              Sei <span className="italic text-[#C9A961]">{membership?.tier}</span>.
            </h1>
            <p className="text-white/70 mb-10">
              La tua iscrizione è attiva fino al <strong>{new Date(membership?.expires_at).toLocaleDateString("it-IT", { day: "numeric", month: "long", year: "numeric" })}</strong>.
              Riceverai a breve l'email di benvenuto con i dettagli della tua welcome box.
            </p>
            <div className="flex gap-4 justify-center flex-wrap">
              <Link to="/club" className="px-8 py-4 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.25em] text-xs hover:bg-white transition-colors">
                Esplora il Club
              </Link>
              <Link to="/area-utente" className="px-8 py-4 border border-white/20 text-white/80 font-semibold uppercase tracking-[0.25em] text-xs hover:border-[#C9A961] hover:text-[#C9A961]">
                Area Utente
              </Link>
            </div>
          </div>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <div data-testid="club-success-fail">
            <XCircle className="h-16 w-16 mx-auto mb-8 text-red-400" strokeWidth={1} />
            <h1 className="editorial-heading text-4xl font-light mb-6">Qualcosa non è andato</h1>
            <p className="text-white/60 mb-8">
              {status === "expired" && "La sessione di pagamento è scaduta."}
              {status === "timeout" && "Non siamo riusciti a confermare il pagamento. Se il pagamento è andato a buon fine, la tua iscrizione sarà comunque attivata entro pochi minuti."}
              {status === "error" && "Sessione non valida."}
            </p>
            <Link to="/club" className="inline-block px-8 py-4 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.25em] text-xs hover:bg-white transition-colors">
              Torna al Club
            </Link>
          </div>
        )}
        {status === "login_required" && (
          <div>
            <h1 className="editorial-heading text-3xl font-light mb-6">Accedi per verificare l'iscrizione</h1>
            <Link to={`/login?redirect=/club/success?session_id=${sessionId}`} className="inline-block px-8 py-4 bg-[#C9A961] text-black font-semibold uppercase tracking-[0.25em] text-xs hover:bg-white transition-colors">
              Accedi
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
